import { useState, useEffect } from 'react';
import { BREAKPOINTS } from './breakpoints.js';

/**
 * useMedia hook to easily use media queries into React components
 * @param query - media query string (ex. useMedia('(prefers-reduced-motion: reduce)'))
 * @param [defaultMatches=false] - initial value that will be returned on the first render.
 */
const useMedia = (query, defaultMatches = false) => {
    const [matches, setMatches] = useState(defaultMatches);
    useEffect(() => {
        if (typeof window === 'undefined')
            return;
        const mediaQuery = window.matchMedia(query);
        let active = true;
        const listener = () => {
            if (!active) {
                return;
            }
            if (mediaQuery.matches) {
                setMatches(true);
            }
            else {
                setMatches(false);
            }
        };
        mediaQuery.addEventListener('change', listener);
        setMatches(mediaQuery.matches);
        // eslint-disable-next-line consistent-return
        return () => {
            active = false;
            mediaQuery.removeEventListener('change', listener);
        };
    }, [query]);
    return matches;
};
/**
 * useXlg return true if screen width less than 1540px
 * @param [defaultMatches=false] - Boolean parameter to force first render return value. May be useful for the cases where you want to prevent render of some component on desired screen size.
 * @return boolean
 */
const useXlg = (defaultMatches = false) => useMedia(BREAKPOINTS.xlg, defaultMatches);
/**
 * useLg return true if screen width less than 1276px
 * @param [defaultMatches=false] - Boolean parameter to force first render return value. May be useful for the cases where you want to prevent render of some component on desired screen size.
 * @return boolean
 */
const useLg = (defaultMatches = false) => useMedia(BREAKPOINTS.lg, defaultMatches);
/**
 * useMd return true if screen width less than 1000px
 * @param [defaultMatches=false] - Boolean parameter to force first render return value. May be useful for the cases where you want to prevent render of some component on desired screen size.
 * @return boolean
 */
const useMd = (defaultMatches = false) => useMedia(BREAKPOINTS.md, defaultMatches);
/**
 * useSm return true if screen width less than 640px
 * @param [defaultMatches=false] - Boolean parameter to force first render return value. May be useful for the cases where you want to prevent render of some component on desired screen size.
 * @return boolean
 */
const useSm = (defaultMatches = false) => useMedia(BREAKPOINTS.sm, defaultMatches);
/**
 * useBreakpoint hook return function on which we can declare value for required breakpoints
 * example: const bp = useBreakpoints(); const adaptiveValue = bp('Default value', { md: 'Value for Tablets' })
 * @return function with following params (defaultValue, { [xlg]: valurForXlg, [lg]: valueForLg, [md]: valueForMd, [sm]: valueForSm })
 */
const useBreakpoint = () => {
    const breakpoints = [
        { size: 'sm', match: useSm() },
        { size: 'md', match: useMd() },
        { size: 'lg', match: useLg() },
        { size: 'xlg', match: useXlg() }
    ];
    return (defaultValue, breakpointValues = {}) => {
        const declaredMatch = breakpoints.find(bp => bp.match && bp.size in breakpointValues);
        return declaredMatch
            ? breakpointValues[declaredMatch.size]
            : defaultValue;
    };
};

export { useBreakpoint, useLg, useMd, useMedia, useSm, useXlg };
